<template>
  <div>
    <LoadingSpinner :isLoading="loading" />
    <template v-if="!loading">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <h2 class="text-2xl font-semibold text-secondary-800">{{ $t("securone.broadcast.title") }}</h2>
        <span class="text-xs text-secondary-500">{{ customer ? customer.name : "" }}<template v-if="departmentLabel"> · {{ departmentLabel }}</template></span>
      </div>
      <p class="text-sm text-secondary-500 mt-1 mb-6">{{ $t("securone.broadcast.subtitle") }}</p>

      <div class="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <!-- 1 · choose -->
        <div>
          <h3 class="so-step">{{ $t("securone.broadcast.step_choose") }}</h3>
          <div v-if="reachable.length" class="bg-white border border-secondary-200 rounded divide-y divide-secondary-100 overflow-hidden">
            <button
              v-for="c in reachable"
              :key="c._id"
              type="button"
              class="w-full flex items-center gap-3 px-4 py-3 text-left border-l-4 focus:outline-none transition-colors"
              :class="selectedId === c._id ? 'border-l-securdia-blue bg-blue-50' : 'border-l-transparent hover:bg-secondary-50'"
              @click="select(c)"
            >
              <div class="w-9 h-9 rounded bg-white border border-secondary-200 flex items-center justify-center text-secondary-700 flex-shrink-0"><BaseIcon :icon="categoryIcon(c.icon)" /></div>
              <div class="min-w-0 flex-1">
                <div class="text-sm font-semibold text-secondary-800">{{ c.name }}</div>
                <div class="text-xs text-secondary-500 truncate">{{ c.instruction }}</div>
              </div>
              <div class="hidden md:flex flex-wrap justify-end gap-1" style="max-width: 40%">
                <SoChip v-for="g in c.groupsDetail" :key="g._id" tone="plain">{{ g.name }}</SoChip>
              </div>
              <BaseIcon icon="chevron-right" class="text-secondary-400 flex-shrink-0" />
            </button>
          </div>
          <SoNote v-else tone="info">{{ $t("securone.overview.nothing_reachable") }}</SoNote>
          <SoNote tone="muted" icon="info-circle" class="mt-4">{{ $t("securone.broadcast.rule_note") }}</SoNote>
        </div>

        <!-- 2 · check and send -->
        <div>
          <h3 class="so-step">{{ $t("securone.broadcast.step_check") }}</h3>
          <div v-if="category" class="bg-white border border-secondary-200 rounded p-5 flex flex-col gap-4">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded bg-secondary-50 border border-secondary-200 flex items-center justify-center text-secondary-700"><BaseIcon :icon="categoryIcon(category.icon)" /></div>
              <div>
                <div class="text-lg font-semibold text-secondary-800">{{ category.name }}</div>
                <div class="text-xs text-secondary-500">{{ scopeLine(category) }}</div>
              </div>
            </div>

            <div>
              <div class="text-xs font-semibold text-secondary-700 mb-1">{{ $t("securone.broadcast.instruction_label") }}</div>
              <div class="rounded border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-secondary-800 leading-relaxed">{{ category.instruction || "—" }}</div>
            </div>

            <div>
              <div class="text-xs font-semibold text-secondary-700 mb-1">{{ $t("securone.broadcast.recipients") }}</div>
              <div class="divide-y divide-secondary-100">
                <div v-for="g in category.groupsDetail" :key="g._id" class="flex items-center gap-2 py-1.5 text-sm">
                  <BaseIcon icon="users" class="text-secondary-400 text-xs" />
                  <span class="text-secondary-800">{{ g.name }}</span>
                  <span class="ml-auto font-mono text-xs text-secondary-500">{{ g.memberCount - g.smsCount }}<template v-if="g.smsCount"> · {{ g.smsCount }} SMS</template></span>
                </div>
                <div v-for="d in category.documentsDetail || []" :key="d._id" class="flex items-center gap-2 py-1.5 text-sm">
                  <BaseIcon icon="file-alt" class="text-secondary-400 text-xs" />
                  <button type="button" class="text-secondary-800 hover:underline text-left" @click="preview(d)">{{ d.title }}</button>
                  <span class="ml-auto font-mono text-xs text-secondary-500">v{{ d.currentVersion }} · {{ $t("securone.broadcast.attached").toLowerCase() }}</span>
                </div>
              </div>
            </div>

            <div class="flex flex-wrap gap-1.5">
              <SoChip v-if="category.reach.sms">{{ $t("securone.broadcast.channel_sms") }}</SoChip>
              <SoChip>{{ $t("securone.broadcast.channel_app") }}</SoChip>
              <SoChip>{{ $t("securone.broadcast.channel_ack") }}</SoChip>
            </div>

            <SoNote v-if="error" tone="bad">{{ error }}</SoNote>

            <div class="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-secondary-100">
              <div class="text-sm text-secondary-700">
                <b class="font-semibold">{{ $t("securone.broadcast.reaches_line", { people: category.reach.people, groups: category.reach.groups }) }}</b>
                <template v-if="category.reach.sms"> · {{ $t("securone.broadcast.sms_count", { n: category.reach.sms }) }}</template>
              </div>
              <div class="flex items-center gap-2">
                <HoldButton ref="hold" tone="success" icon="paper-plane" :label="$t('securone.broadcast.trigger', { name: category.name })" :disabled="!category.reach.groups" :sending="sending" @fire="fire" />
                <router-link :to="{ name: 'securone_overview' }" class="sd-btn sd-btn--secondary">{{ $t("securone.common.cancel") }}</router-link>
              </div>
            </div>
            <p class="text-xs text-red-600">{{ $t("securone.broadcast.cannot_recall") }} {{ $t("securone.broadcast.no_countdown") }}</p>
          </div>
          <div v-else class="bg-white border border-dashed border-secondary-300 rounded p-10 text-center text-sm text-secondary-500">{{ $t("securone.broadcast.pick_one") }}</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import SoNote from "./components/SoNote.vue";
import SoChip from "./components/SoChip.vue";
import HoldButton from "./components/HoldButton.vue";
import api from "./securone.api";
import * as notifications from "./securone.notifications";
import { categoryIcon, errorMessage } from "./securone.helpers";

// D-10 on the desktop, in two steps: choose one of the categories I can reach
// (AR1 - the server enforces the same rule), check what goes out and to whom,
// then hold to send. /send/:id preselects a category (from the overview).
export default {
  name: "SecurOneBroadcastConfirm",
  components: { SoNote, SoChip, HoldButton },
  data() {
    return { loading: true, reachable: [], selectedId: this.$route.params.id || null, sending: false, error: null };
  },
  computed: {
    customer() {
      return this.$store.getters["securone/customer"];
    },
    departments() {
      return this.$store.getters["securone/departments"];
    },
    departmentLabel() {
      if (this.departments.length === 1) return this.departments[0].name;
      return this.departments.length > 1 ? this.$tc("securone.context.departments_count", this.departments.length, { n: this.departments.length }) : "";
    },
    category() {
      return this.reachable.find((c) => c._id === this.selectedId) || null;
    },
  },
  methods: {
    categoryIcon,
    scopeLine(c) {
      return c.scope === "departments" && c.departmentNames && c.departmentNames.length ? this.$t("securone.broadcast.scope_departments", { names: c.departmentNames.join(", ") }) : this.$t("securone.broadcast.scope_customer");
    },
    async load() {
      this.loading = true;
      try {
        const reachable = await api.categories({ reachable: true });
        this.reachable = reachable.filter((c) => c.isActive !== false);
        if (this.selectedId && !this.reachable.some((c) => c._id === this.selectedId)) this.selectedId = null;
      } catch (error) {
        this.handleError(error, errorMessage(this, error));
      } finally {
        this.loading = false;
      }
    },
    select(c) {
      this.error = null;
      this.selectedId = c._id;
      if (this.$route.params.id !== c._id) this.$router.replace({ name: "securone_broadcast", params: { id: c._id } }).catch(() => {});
    },
    async preview(d) {
      try {
        const file = await api.documentFile(d._id, "current");
        window.open(file.url, "_blank", "noopener");
      } catch (error) {
        this.handleError(error, errorMessage(this, error));
      }
    },
    async fire() {
      if (this.sending || !this.category) return;
      this.sending = true;
      this.error = null;
      try {
        const event = await api.createEvent({ categoryId: this.category._id });
        notifications.markSeen(event._id); // my own alarm must not notify me
        this.$toasted.show(this.$t("securone.broadcast.sent_toast", { n: event.recipientCount }), { type: "success", theme: "outline", position: "top-right", duration: 6000 });
        this.$router.push({ name: "securone_event", params: { id: event._id } });
      } catch (error) {
        this.error = errorMessage(this, error, "securone.errors.broadcast_failed");
        this.sending = false;
        if (this.$refs.hold) this.$refs.hold.reset();
      }
    },
  },
  watch: {
    "$route.params.id"(id) {
      if (id && id !== this.selectedId) this.selectedId = id;
    },
  },
  created() {
    this.setPageTitle("SecurOne · " + this.$t("securone.broadcast.title"), "securone_broadcast");
    this.load();
  },
};
</script>
